import { ListFilter } from "lucide-react";

import { Button } from "components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { NewAdvancedSearch } from "components/NewAdvancedSearch";

// Saved filters are keyed per model type: an option set valid on a Contact is
// not valid on a Case, so a filter saved on one must not be offered on the
// other.
function savedFilterTableName(modelType) {
  const snakeCase = String(modelType)
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase();
  return `audit_log_${snakeCase}`;
}

function DisabledFiltersButton() {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          // focusableWhenDisabled keeps the button in the tab order so keyboard
          // users still reach the tooltip; Base UI then reports the state
          // through data-disabled instead of the native attribute, so the
          // disabled look is keyed off that. No pointer-events-none, so hover
          // still opens the tooltip.
          <Button
            variant="outline"
            size="icon"
            disabled
            focusableWhenDisabled
            aria-label="Filters"
            className="tw:data-disabled:cursor-not-allowed tw:data-disabled:opacity-50"
          />
        }
      >
        <ListFilter />
      </TooltipTrigger>
      <TooltipContent>Filters</TooltipContent>
    </Tooltip>
  );
}

/**
 * The Activity Log's Filters button and panel.
 *
 * The panel rebuilds its form only when the URL has changed since it last
 * opened, not when descriptors arrive. Opening it before they load would latch
 * it empty and let Apply write away the active filters, so it is not offered
 * until it has them.
 */
export function AuditLogFilters({
  filters,
  queryParams,
  modelType,
  onApply,
  isLoading,
  isError,
}) {
  if (isError) {
    return (
      <div className="tw:flex tw:items-center tw:gap-2">
        <span className="tw:text-sm tw:text-(--color-text-muted)">
          Filters unavailable
        </span>
        <DisabledFiltersButton />
      </div>
    );
  }
  if (isLoading) {
    return <DisabledFiltersButton />;
  }
  return (
    <NewAdvancedSearch
      filters={filters}
      queryParams={queryParams}
      tableName={savedFilterTableName(modelType)}
      onApply={onApply}
    />
  );
}
